import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {memo, useMemo, useState} from 'react';

import {IconPositioningContextProvider} from '../../state/issues/IconPositioningContext';
import {prettyXAxisLabel} from '../../util/plotHelpers/axis';
import {
  getPlotFontSize,
  plotFontSizeToPx,
} from '../../util/plotHelpers/plotFontSize';
import {useResizeObserverDebounced} from '../../util/useResizeObserverDebounced';
import {PanelTitleView} from '../PanelTitle/PanelTitleView';
import {EMPTY_ANNOTATION_HOVER, EMPTY_ANNOTATIONS} from '../vis/annotations';
import {
  presentChartSeries,
  presentNumericLines,
  resolveNumericSeries,
} from '../vis/chartSeries';
import {LinePlot} from '../vis/LinePlot';
import {BarChart} from './BarChart';
import {DelayedMetricsNotice} from './DelayedMetricsNotice';
import {getErrorMessage} from './getGraphError';
import {partitionLines} from './graphLines';
import {
  DEFAULT_GRAPH_LEGEND_POSITION,
  type GraphNotices,
  type GraphRenderProps,
} from './graphRenderProps';
import {IssueMarkerList} from './IssueMarkerList';
import {getIssuesWrapperStatus} from './issueMarkers';
import {PlotLayoutView} from './Layout';
import {LinePlotLegendView} from './LinePlotLegendView';
import {
  LinePlotLegendWarningView,
  type LinePlotLegendWarningViewProps,
} from './LinePlotLegendWarningView';
import {
  type GraphRendererSettings,
  LinePlotSettingsProvider,
} from './LinePlotSettingsContext';
import {MissingRunBanner} from './MissingRunBanner';
import {SamplingWarningView} from './SamplingWarningView';
import {getIsLegendShown} from './useLegendVisibility';
import {useLinesForDisplay} from './useLinesForDisplay';
import {
  linesHaveRenderableData,
  useRegexMetricSettlingLoading,
} from './useRegexMetricSettlingLoading';
import {isBarPlot} from './utils/checkTypes';

const expandStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
};

const SAMPLING_WARNING_STYLE: React.CSSProperties = {zIndex: 1};

const GraphComp: React.FC<GraphRenderProps> = props => {
  // destructure
  const {series, seriesStylesById, seriesMetadataById} = props;
  const annotations = props.annotations;
  const annotationHoverSource = {
    get: annotations?.readHoveredId ?? EMPTY_ANNOTATION_HOVER.get,
    subscribe:
      annotations?.subscribeToHover ?? EMPTY_ANNOTATION_HOVER.subscribe,
  };
  const issueLines = annotations?.items ?? EMPTY_ANNOTATIONS;
  const notices = props.notices;

  // Keep each mounted notice dependent only on the fields it displays.
  const limitNotices: LinePlotLegendWarningViewProps['notices'] = {
    displayRowLimitPerRunset: props.notices?.displayRowLimitPerRunset,
    fetchedRunCount: props.notices?.fetchedRunCount,
    groupedIncludedRunCount: props.notices?.groupedIncludedRunCount,
    groupInputRunLimitPerRunset: props.notices?.groupInputRunLimitPerRunset,
    groupingDepth: props.notices?.groupingDepth,
    hasGroupedRunsets: props.notices?.hasGroupedRunsets,
    hasSubsampledGroups: props.notices?.hasSubsampledGroups,
    maxFetchedRunsPerRunset: props.notices?.maxFetchedRunsPerRunset,
    metricCount: props.notices?.metricCount,
    runsetCount: props.notices?.runsetCount,
    // Show guidance for changing chart limits unless the caller disables it.
    // The app adapter disables this help for read-only charts.
    showLimitHelp: props.notices?.showLimitHelp ?? true,
  };
  const samplingNotices: GraphNotices = {
    isSamplingFallback: props.notices?.isSamplingFallback,
    samplingWarnings: props.notices?.samplingWarnings,
  };
  const delayedNotices: GraphNotices = {
    onDelayedNoticeShown: props.notices?.onDelayedNoticeShown,
    onLoadAnyway: props.notices?.onLoadAnyway,
  };

  // state
  const [isHovered, setIsHovered] = useState(false);

  const {
    isResizing,
    height = 1,
    ref: elementRef,
    width = 1,
  } = useResizeObserverDebounced<HTMLDivElement>(1);

  const axis = props.axis;
  // Issue lines past a zoomed x bound are hidden, and a zoomed x axis skips
  // the domain mismatch check.
  const isZoomedXMin = props.zoom.isZoomedXMin;
  const isZoomedXMax = props.zoom.isZoomedXMax;

  const fontSize = getPlotFontSize(props.appearance.fontSize, height);

  // Additional constraints are applied for legends depending on panel width/height and if
  // view is single run
  const isLegendShown =
    props.legend != null &&
    getIsLegendShown({
      isMultiMetric: props.plotSettings.isMultiMetric,
      isSingleRun: props.legend.isSingleRun ?? false,
      panelSize: {height, width},
      showLegend: props.legend.show,
    });
  const legendPosition =
    props.legend?.position ?? DEFAULT_GRAPH_LEGEND_POSITION;

  const numericLines = useMemo(() => resolveNumericSeries(series), [series]);
  const lines = useMemo(
    () =>
      presentChartSeries(numericLines, seriesStylesById, seriesMetadataById),
    [numericLines, seriesStylesById, seriesMetadataById]
  );
  const timestep = numericLines[0]?.timestep;

  const {filteredLines, filteredNumericLines} = useLinesForDisplay(
    numericLines,
    lines,
    {
      xScale: axis.xScale,
      yScale: axis.yScale,
      isFullFidelity: props.rendering.isFullFidelity,
      isHovered,
    }
  );
  const isRegexMetricSettling = useRegexMetricSettlingLoading(
    props.loading.regexSettlingKey,
    filteredNumericLines
  );
  const effectiveLoading = props.loading.isActive || isRegexMetricSettling;

  const {
    legendLines: numericLegendLines,
    visibleLines: numericVisibleLines,
    hiddenLines: numericHiddenLines,
  } = useMemo(
    function selectNumericLinesForDisplay() {
      return partitionLines(filteredNumericLines);
    },
    [filteredNumericLines]
  );
  const legendLines = presentNumericLines(numericLegendLines, filteredLines);
  const visibleLines = useMemo(
    () => presentNumericLines(numericVisibleLines, filteredLines),
    [numericVisibleLines, filteredLines]
  );
  const hiddenLines = presentNumericLines(numericHiddenLines, filteredLines);
  const zoom = props.zoom;

  // derived values
  const [errorDetail, errorMessage] = getErrorMessage(
    props.errors,
    notices?.isDelayed === true,
    filteredNumericLines,
    zoom
  );

  const renderBarChart = isBarPlot(
    numericLines,
    zoom.isZooming,
    props.fallbacks?.singlePointStyle
  );
  const renderPanelError = !effectiveLoading && errorMessage != null;

  const [isDrawing, setIsDrawing] = useState(false);

  const panelErrorMetaInfo = {isFullFidelity: props.rendering.isFullFidelity};

  const warningPanel = {
    isMultiMetric: props.plotSettings.isMultiMetric ?? false,
  };
  const warningRendering = {isFullFidelity: props.rendering.isFullFidelity};
  const warning = notices?.displayRowLimitPerRunset !== undefined && (
    <LinePlotLegendWarningView
      notices={limitNotices}
      panel={warningPanel}
      rendering={warningRendering}
      loading={effectiveLoading}
      lines={legendLines}
      disallowSelect={isDrawing}
    />
  );

  if (
    renderBarChart ||
    renderPanelError ||
    (props.errors.hasUnexpectedError && !effectiveLoading)
  ) {
    return (
      <div
        className="chart"
        data-test={`runs-line-plot-error-${
          props.errors.savedXKey ?? 'unknown-x-axis'
        }`}
        onMouseEnter={function markErrorPanelHovered() {
          setIsHovered(true);
        }}
        onMouseLeave={function clearErrorPanelHovered() {
          setIsHovered(false);
        }}
        ref={elementRef}>
        {props.title && (
          <PanelTitleView
            title={props.title.text}
            fontSize={fontSize}
            highlightSearch={props.title.renderHighlightedText}
            onTitleClick={props.title.onActivate}
          />
        )}
        {renderBarChart && warning}
        <div className="chart-content" data-test="chart-content">
          {renderPanelError ? (
            <PanelError
              panelViewType="Run History Line Plot"
              panelID={props.panelId}
              metaInfo={panelErrorMetaInfo}
              message={
                props.errors.hasUnexpectedError ? (
                  <div>
                    An unexpected error was detected. Will try again shortly.
                  </div>
                ) : (
                  errorMessage
                )
              }
              logErrorMessage={
                props.errors.hasUnexpectedError
                  ? 'An unexpected error was detected. Will try again shortly.'
                  : typeof errorDetail === 'string'
                    ? errorDetail
                    : 'Chart error occurred'
              }
            />
          ) : (
            <BarChart
              lines={lines}
              min={props.fallbacks?.barYMin}
              max={props.fallbacks?.barYMax}
            />
          )}
        </div>
      </div>
    );
  }

  const issueStatus = getIssuesWrapperStatus({
    annotationActive: annotations?.isActive ?? false,
    issueCount: annotations?.activeCount ?? 0,
    issueRampStatus: annotations?.isEnabled ? 'enabled' : 'disabled',
  });

  return (
    <div
      data-x-axis={props.errors.savedXKey ?? 'unknown-x-axis'}
      data-test="chart-content"
      ref={elementRef}
      style={expandStyles}>
      <IconPositioningContextProvider issues={issueLines} width={width}>
        <PlotLayoutView
          delayed={notices?.isDelayed === true}
          DelayWarning={
            notices?.isDelayed === true && (
              <DelayedMetricsNotice notices={delayedNotices} />
            )
          }
          IssueMarkers={
            annotations && <IssueMarkerList annotations={annotations} />
          }
          issueStatus={issueStatus}
          fontSize={plotFontSizeToPx.legend[fontSize]}
          handleHover={setIsHovered}
          loading={effectiveLoading}
          numLines={
            linesHaveRenderableData(visibleLines) ? visibleLines.length : 0
          }
          Graph={
            isResizing ? (
              <div />
            ) : (
              <LinePlot
                excludeOutliers={axis.outlierPolicy}
                filteredLines={visibleLines}
                numericLines={numericVisibleLines}
                fontSize={fontSize}
                highlightSource={props.highlight.source}
                isDrawing={isDrawing}
                isHovered={isHovered}
                issueLines={issueLines}
                hoveredIssueSource={annotationHoverSource}
                isZoomedXMax={isZoomedXMax}
                isZoomedXMin={isZoomedXMin}
                panelId={props.panelId}
                tooltip={props.tooltip}
                setHighlights={props.highlight.setHighlights}
                setIsDrawing={setIsDrawing}
                svg={props.rendering.exportMode}
                timestep={timestep}
                xAxis={prettyXAxisLabel(axis.xKey, numericLines)}
                xAxisTitle={props.axis.xTitle}
                xDomain={zoom.xDomain}
                xScale={axis.xScale}
                yAxis={''}
                yAxisTitle={props.axis.yTitle}
                yDomain={zoom.yDomain}
                yScale={axis.yScale}
                zoomCallback={zoom.onZoom}
                zooming={zoom.isZooming}>
                {
                  <MissingRunBanner
                    highlightSource={props.highlight.source}
                    lines={visibleLines}
                  />
                }
              </LinePlot>
            )
          }
          legendPosition={legendPosition}
          Legend={
            props.legend && (
              <LinePlotLegendView
                fontSize={fontSize}
                hiddenLines={hiddenLines}
                isMultiMetric={props.plotSettings.isMultiMetric}
                lines={legendLines}
                isVertical={!['east', 'west'].includes(legendPosition)}
                disallowSelect={isDrawing}
                legend={props.legend}
              />
            )
          }
          showLegend={isLegendShown}
          sizeRef={props.sizeRef}
          Title={
            props.title && (
              <PanelTitleView
                title={props.title.text}
                highlightSearch={props.title.renderHighlightedText}
                onTitleClick={props.title.onActivate}
                fontSize={fontSize}
                disallowSelect={isDrawing}
              />
            )
          }
          Warning={warning}
        />
      </IconPositioningContextProvider>
      {notices?.isSamplingFallback !== undefined && (
        <SamplingWarningView
          notices={samplingNotices}
          style={SAMPLING_WARNING_STYLE}
          width={width}
        />
      )}
    </div>
  );
};
const Graph = memo(GraphComp);
Graph.displayName = 'Graph';

// TODO(axel): This wrapper is only here because I'm not confident that some props are guaranteed to exist.
// Find out what's up and kill this wrapper.
const GraphWrapperComp: React.FC<GraphRenderProps> = function GraphWrapperComp(
  props
) {
  if (!props.loading.isReady) {
    return <p>No Histories</p>;
  }

  const settings: GraphRendererSettings = {
    ...props.plotSettings,
    isDarkMode: props.appearance.isDarkMode,
    forceReactVis: props.rendering.forceReactVis,
    isFullFidelity: props.rendering.isFullFidelity,
    pointVisualizationMethod: props.rendering.pointStrategy,
    plotType: props.rendering.plotType,
    uplotEnabled: props.rendering.allowUPlot,
    xAxis: props.axis.xKey,
    xAxisIsDatetime: props.axis.xIsDatetime,
    xLogScale: props.axis.xScale === 'log',
    yLogScale: props.axis.yScale === 'log',
  };
  return (
    <LinePlotSettingsProvider settings={settings}>
      <Graph {...props} />
    </LinePlotSettingsProvider>
  );
};
export const GraphWrapper = memo(GraphWrapperComp);
GraphWrapper.displayName = 'GraphWrapper';
